/**
 * defa.controllers.UserController
 * The Controller for the UserRouter, all front end urls starting with /accounts/.
 * @version 1.2.0
 */
import { defa }            from 'defa-object';
import PhoneNumbers        from 'scripts/defa/phone-numbers/collection-phone-numbers';
import PhoneNumber         from 'scripts/defa/phone-numbers/model-phone-number';
import User                from 'scripts/defa/users/model-user';
import UserContainer       from 'scripts/defa/users/view-user-container';
import UserNewPasswordForm from 'scripts/defa/users/view-user-new-password-form';
import UserVerifyEmailForm from 'scripts/defa/users/view-user-verify-email-form';
import Controller          from 'scripts/poppet/controller';

const channelUser   = Bb.Radio.channel( 'user' );
const channelNav    = Bb.Radio.channel( 'nav' );
const channelLayout = Bb.Radio.channel( 'layout' );

const apiHost = defa.vars.apiHost;
const api     = {
	logout: apiHost + '/logout',
};
const channel = Bb.Radio.channel( 'objects' );

const UserController = Controller.extend( {
	
	// ------------------------------------------------
	// Routes
	// ------------------------------------------------
	
	// - logged in users / must be logged in to access
	
	/**
	 * Shows the UserPanel for the currently logged in user.
	 * @void
	 */
	showUserPanel: function ( id ) {
		
		let userId = ( 'me' === id )
					 ? channelUser.request( 'id' )
					 : id;
		
		this.requireUser( {
			userId  : userId,
			callback: this.attachUserContainer,
			view    : 'user:panel',
			complete: true,
			sync    : false,
		} );
	},
	
	/**
	 * Show the language card.
	 * @void
	 */
	showLanguage: function ( id ) {
		
		let userId = ( 'me' === id )
					 ? channelUser.request( 'id' )
					 : id;
		
		this.requireUser( {
			userId  : userId,
			callback: this.attachUserContainer,
			view    : 'user:language',
			complete: true,
			sync    : false,
		} );
	},
	
	/**
	 * Shows the password card with the User Panel.
	 * @void
	 */
	showPassword: function ( id ) {
		
		let userId = ( 'me' === id )
					 ? channelUser.request( 'id' )
					 : id;
		
		this.requireUser( {
			userId  : userId,
			callback: this.attachUserContainer,
			view    : 'user:password',
			complete: true,
		} );
	},
	
	showEmail: function(id) {
		let userId = ( 'me' === id )
					 ? channelUser.request( 'id' )
					 : id;
		
		this.requireUser( {
			userId  : userId,
			callback: this.attachUserContainer,
			view    : 'user:email',
			complete: true,
		} );
	},
	
	// - login related views / no access for logged in users
	
	/**
	 * Creates new account and forwards to home.
	 * @void
	 */
	showCreate: function () {
		
		this.currentUser = new User();
		
		this.attachUserContainer( {
			view       : 'user:create',
			currentUser: this.currentUser,
			noLogin    : true,
			closeLink  : {
				event: 'user:thankyou',
				param: []
			},
		} );
	},
	
	/**
	 * Shows the Forgot Password view.
	 * @void
	 */
	showForgotPassword: function () {
		this.attachUserContainer( {
			view     : 'user:forgot',
			user     : channelUser.request( 'model' ),
			noLogin  : true,
			sync     : false,
			closeLink: {
				event: 'unit:list',
			},
		} );
		
	},
	
	/**
	 * Routes the user through a login procedure, forwards to home by default.
	 * @void
	 */
	showLogin: function ( email ) {
		email = email || '';
		
		this.attachUserContainer( {
			view     : 'user:login',
			user     : channelUser.request( 'model' ),
			email    : email,
			noLogin  : true,
			sync     : false,
			closeLink: {
				event: 'unit:list',
			},
		} );
	},
	
	/**
	 * Shows the dual login/register view:
	 * @void
	 */
	showLoginCreate: function () {
		
		// not available for logged in users
		//var user = channel.request( 'user' );
		
		if ( channelUser.request( 'is:logged:in' ) ) {
			channelNav.trigger( 'unit:list', false, { reload: true } );
		} else {
			
			let user         = channelUser.request( 'model' );
			this.currentUser = new User();
			
			// reload page on successfull login
			this.listenTo( user, 'change', function () {
				channelNav.trigger( 'unit:list', false, { reload: true } );
			} );
			
			this.attachUserContainer( {
				view                : 'user:logincreate',
				user                : user,                                  // the login user
				currentUser         : this.currentUser,                      // the create user
				isBluetoothAvailable: channelLayout.request( 'is:native' ),  // enables the bluetooth scan nav trigger
																			 // in the login view.
				noLogin             : true,
				closeLink           : {
					event: 'user:thankyou', // this closelink is only for the create part of the view!
				},
			} );
		}
	},
	
	/**
	 * Logs out the current user and forwards to home.
	 * @void
	 */
	showLogout: function () {
		
		channelLayout.request( 'root' ).getRegion( 'main' ).empty();
		
		$.get( api.logout )
		 .done( function ( response ) {
			 channelNav.trigger( '/', false, { reload: true } );
		 } );
	},
	
	/**
	 * Shows the thank you for signing up view.
	 * @void
	 */
	showThankYou: function () {
		if ( channelUser.request( 'is:logged:in' ) ) {
			channelNav.trigger( 'unit:list' );
		} else {
			
			this.attachUserContainer( {
				view       : 'user:thank-you',
				currentUser: this.currentUser, //channelUser.request( 'model' ),
				noLogin    : true,
				sync       : false,
			} );
		}
		
	},
	
	// - phone number related functions
	
	/**
	 * Shows the phone numbers of a single user.
	 * @param {int} id  The id of the User.
	 * @void
	 */
	showPhoneList: function ( id ) {
		
		let userId = ( 'me' === id )
					 ? channelUser.request( 'id' )
					 : id;
		
		this.requireUser( {
			userId   : userId,
			callback : this.attachUserContainer,
			view     : 'user:phone',
			phoneView: 'phone:list',
		} );
	},
	
	/**
	 * Shows a view to create a new phone number.
	 * @param {int} id  The id of the User.
	 * @void
	 */
	showPhoneCreate: function ( id ) {
		
		let userId = ( 'me' === id )
					 ? channelUser.request( 'id' )
					 : id;
		
		let phoneNumber = new PhoneNumber();
		
		this.requireUser( {
			userId     : userId,
			callback   : this.attachUserContainer,
			view       : 'user:phone',
			phoneView  : 'phone:create',
			phoneNumber: phoneNumber,
		} );
	},
	
	/**
	 * Updates a single phone number.
	 * @param {int} id      The id of the User.
	 * @param {int} number  The id of the PhoneNumber.
	 * @void
	 */
	showPhoneUpdate: function ( id, number ) {
		
		let userId = ( 'me' === id )
					 ? channelUser.request( 'id' )
					 : id;
		
		this.requirePhoneNumber( {
			phoneNumberId: number,
			callback     : this.attachUserContainer,
			view         : 'user:phone',
			phoneView    : 'phone:update',
			userId       : userId,
		} );
	},
	
	/**
	 * Updates a single phone number of a single user.
	 * @param {int} id      The id of the User.
	 * @param {int} number  The id of the PhoneNumber.
	 * @void
	 */
	showPhoneDelete: function ( id, number ) {
		
		this.requirePhoneNumber( {
			phoneNumberId: number,
			callback     : this.attachUserContainer,
			view         : 'user:phone',
			phoneView    : 'phone:delete',
			userId       : id,
		} );
	},
	
	// - specials / token-powered
	
	/**
	 * Create new password with a token.
	 * @void
	 */
	showNewPassword: function ( token ) {
		
		let userNewPasswordForm = new UserNewPasswordForm( {
			token    : token,
			closeLink: {
				event: 'user:login',
			},
		} );
		
		userNewPasswordForm.listenTo( userNewPasswordForm, 'password:created', function () {
			userNewPasswordForm.animateOut();
		} );
		
		userNewPasswordForm.listenTo( userNewPasswordForm, 'password:redirect', function( redirectUrl) {
			channelNav.trigger( 'redirect:external', [ redirectUrl, ] , { fallbackEvent: this.closeLink.event } );
		});
		
		userNewPasswordForm.listenTo ( userNewPasswordForm, 'password:internal', function(internalPath) {
			channelNav.trigger('redirect:internal', [ internalPath, ], {trigger: true, fallbackEvent: this.closeLink.event} );
		});
		
		
		channelLayout.request( 'root' ).showChildView( 'main', userNewPasswordForm );
	},
	
	/**
	 * Create new password with a token.
	 * @void
	 */
	showVerifyEmail: function ( token ) {
		
		let userVerifyEmailForm = new UserVerifyEmailForm( {
			token    : token,
			closeLink: {
				event: 'user:me',
			},
		} );
		
		userVerifyEmailForm.listenToOnce( userVerifyEmailForm, 'email:verified', function () {
			
			// email verified, reload application
			this.listenToOnce( userVerifyEmailForm, 'animateOut', function () {
				_.extend( userVerifyEmailForm.closeLink, {
					options: {
						reload: true,
					},
				} );
			} );
			userVerifyEmailForm.animateOut();
			
		} );
		
		channelLayout.request( 'root' ).showChildView( 'main', userVerifyEmailForm );
		
	},
	
	
	// ------------------------------------------------
	// Event handlers
	// ------------------------------------------------
	
	// - sync functions
	/**
	 * Triggers when a phone number sync event bubbles through.
	 * @void
	 */
	onPhoneNumberSync: function ( model ) {
		this.userContainer.animateChildOut( {
			closeLink: {
				event : 'phone:list',
				params: [ model.get( 'user' ) ],
			},
		} );
	},
	
	/**
	 * Triggers when a user sync event bubbles through.
	 * @void
	 */
	onUserSync: function ( model ) {
		this.userContainer.animateChildOut();
	},
	
	
	// ------------------------------------------------
	// Helper functions
	// ------------------------------------------------
	
	// - require functions
	
	/**
	 * Requests a User, before calling the callback.
	 * @param {Object} options  All the needed options, most importantly a userId and a callback function.
	 * @void
	 */
	requireUser: function ( options ) {
		this.requireModel( 'User', defa.models, options );
	},
	
	/**
	 * Requires a PhoneNumber object before continuing it's callback.
	 * @param {Object} options All the needed options, most importantly a phoneNumberId and a callback function.
	 * @void
	 */
	requirePhoneNumber: function ( options ) {
		let self = options.self || this;
		
		// save the final callback before nesting functions.
		if ( !self.endCallback ) {
			self.endCallback = options.callback;
		}
		
		// require a User before requiring a PhoneNumber.
		if ( !options.currentUser ) {
			self.requireUser( _.extend( options, { callback: this.requirePhoneNumber } ) );
		} else {
			// get and remove the final callback before requiring the final model.
			let callback = self.endCallback;
			delete self.endCallback;
			self.requireModel( 'PhoneNumber', defa.models, _.extend( options, { callback: callback } ) );
		}
	},
	
	// - layout functions
	
	/**
	 * Create and display a UserContainer.
	 * @param {Object} options  An object containing all the needed objects for creating, listing and editing
	 *     User-information.
	 * @void
	 */
	attachUserContainer: function ( options ) {
		let user = channelUser.request( 'model' );
		
		options             = options || {};
		options.user        = options.user || user; // this is the logged in user. (can be support) !TODO: maybe always
													// pull from channelUser instead?
		options.currentUser = options.currentUser || user; // this is the user you're looking at
		options.closeLink   = options.closeLink || { event: 'user:panel', params: [ options.userId, ], };
		options.noLogin     = options.noLogin || false;
		
		// route to login if required
		if ( !user.get( 'transientLoggedIn' ) && !options.noLogin ) {
			channelNav.trigger( 'user:login', false, { trigger: true, replace: true, } );
			
			// play with the UserContainer.
		} else {
			
			let self = options.self || this;
			
			// if this is a phoneView, add user's phoneNumbers to the options
			if ( options.phoneView ) {
				
				// populate options with the currentUsers phoneNumbers just in case
				options.phoneNumbers = new PhoneNumbers(
					channel.request( 'phoneNumbers' ).where( {
						user: options.currentUser.get( 'id' ),
					} )
				);
			}
			
			// if the userPanel isn't made or is destroyed, create it and attach it's listeners!
			if ( !( self.userContainer instanceof UserContainer ) || self.userContainer.isDestroyed() ) {
				
				self.userContainer = new UserContainer( options );
				
				// stop all controller-listeners whenever an accompanying layout is destroyed!
				self.listenTo( self.userContainer, 'destroy', function () {
					self.stopListening();
				} );
				
				
				// if it already exists, just update it's options.
			} else {
				self.userContainer.settings( options );
			}
			
			// --------------------------------------------------------------------------------------
			// attach option-objects-listeners
			
			if ( options.currentUser ) {
				// stop listener first.
				self.stopListening( options.currentUser, 'sync', self.onUserSync );
				
				// create new listener as long as sync isn't set to false
				if ( !_.isBoolean( options.sync ) || options.sync ) {
					self.listenTo( options.currentUser, 'sync', self.onUserSync );
				}
			}
			
			if ( options.phoneNumber ) {
				// stop listener first
				self.stopListening( options.phoneNumber, 'sync', self.onPhoneNumberSync );
				
				// create new listener as long as sync isn't set to false
				if ( !_.isBoolean( options.sync ) || options.sync ) {
					self.listenTo( options.phoneNumber, 'sync', self.onPhoneNumberSync );
				}
			}
			
			if ( !self.userContainer.isAttached() ) {
				channelLayout.request( 'root' ).showChildView( 'main', self.userContainer );
			}
		}
	},
	
} );

export default UserController;
